/**
 * Hook que verifica periodicamente transferências recebidas na blockchain
 * e notifica o usuário (quem está cobrando) quando um pagamento chega na carteira.
 * - RPC público (somente leitura): usado sempre; no mobile é rápido e não dispara prompt da carteira.
 * - Provider da carteira: varredura extra só com aba visível (ex.: PC onde o RPC pode estar atrás);
 *   nunca com aba oculta para não empilhar "Ir para MetaMask?" no mobile.
 */

import { useEffect, useRef } from 'react';
import { useContext } from 'react';
import { BrowserProvider, Contract, getAddress } from 'ethers';
import { useWalletConnection } from '@/hooks/useWalletConnection';
import { IncomingTransferContext } from '@/contexts/IncomingTransferContext';
import { useNotifications } from '@/contexts/NotificationsContext';
import { STABLECOIN_METADATA_BY_CHAIN, ERC20_ABI_WITH_TRANSFER_EVENT } from '@/config/stablecoinContracts';
import { getReadOnlyProvider } from '@/utils/publicRpcReadOnly';
import { useSettings } from '@/contexts/SettingsContext';

const POLL_INTERVAL_MS = 8_000;    // 8 segundos (detecção mais rápida para quem está cobrando)
const FIRST_POLL_DELAY_MS = 2_000; // primeira verificação após 2s
const MAX_BLOCKS_RANGE = 500;      // limite de blocos por consulta (evita limite do RPC)
const LOOK_BACK_BLOCKS = 20;       // janela para trás para não perder tx se o RPC público estiver atrás do nó da carteira
const WALLET_SWEEP_BLOCKS = 30;   // varredura pela carteira (só com aba visível): últimos N blocos

const isEVMWallet = (wallet, isPhantomEVM) =>
  wallet === 'metamask' ||
  wallet === 'walletconnect' ||
  (wallet === 'phantom' && isPhantomEVM);

export function useIncomingTransferNotification() {
  const { connectedWallet, walletAddress, selectedNetwork, provider: metamaskProvider, isPhantomEVM } = useWalletConnection();
  const { reportIncomingTransfer } = useContext(IncomingTransferContext) || {};
  const { addNotification } = useNotifications();
  const { disableLegacyIncomingPolling } = useSettings();
  const lastCheckedBlockRef = useRef(null);
  const notifiedTxHashesRef = useRef(new Set());

  useEffect(() => {
    // Quando a flag administrativa estiver ligada, desliga completamente o polling legado
    // e deixa o recebimento a cargo apenas do sistema de notificações via Firestore.
    if (disableLegacyIncomingPolling) {
      return;
    }

    if (!isEVMWallet(connectedWallet, isPhantomEVM) || !walletAddress || !selectedNetwork?.chainId) {
      return;
    }
    if (typeof reportIncomingTransfer !== 'function') return;

    const chainId = selectedNetwork.chainId;
    const tokens = STABLECOIN_METADATA_BY_CHAIN[chainId];
    if (!tokens?.length) return;

    const readOnlyProvider = getReadOnlyProvider(chainId);
    const walletProviderRef = metamaskProvider;

    let walletAddressChecksum;
    try {
      walletAddressChecksum = getAddress(walletAddress);
    } catch {
      walletAddressChecksum = walletAddress;
    }

    const checkIncomingTransfers = async () => {
      try {
        const currentBlock = await readOnlyProvider.getBlockNumber();
        let fromBlock = lastCheckedBlockRef.current == null
          ? Math.max(0, currentBlock - LOOK_BACK_BLOCKS)
          : lastCheckedBlockRef.current + 1;
        fromBlock = Math.max(0, fromBlock - LOOK_BACK_BLOCKS);

        if (fromBlock > currentBlock) {
          lastCheckedBlockRef.current = currentBlock;
          return;
        }

        const toBlock = currentBlock;
        fromBlock = Math.max(fromBlock, toBlock - MAX_BLOCKS_RANGE);
        const notified = notifiedTxHashesRef.current;

        for (const { symbol, address, decimals } of tokens) {
          try {
            const contract = new Contract(address, ERC20_ABI_WITH_TRANSFER_EVENT, readOnlyProvider);
            const filter = contract.filters.Transfer(null, walletAddressChecksum);
            const events = await contract.queryFilter(filter, fromBlock, toBlock);

            for (const event of events) {
              const txHash = event.transactionHash ?? event.log?.transactionHash;
              if (!txHash || notified.has(txHash)) continue;
              const raw = event.args?.value ?? event.args?.[2];
              if (raw == null) continue;
              const amount = Number(raw) / Math.pow(10, decimals);
              if (amount <= 0) continue;
              notified.add(txHash);
              const fromAddress = event.args?.[0] ?? event.args?.from ?? '';
              const payload = {
                amount,
                symbol,
                txHash,
                networkName: selectedNetwork?.name ?? '',
                chainId,
                tokenAddress: address,
                fromAddress: typeof fromAddress === 'string' ? fromAddress : (fromAddress?.toString?.() ?? ''),
                receivedAt: Date.now(),
              };
              reportIncomingTransfer(payload);
              if (typeof addNotification === 'function') {
                addNotification({ type: 'incoming_funds', ...payload });
              }
            }
          } catch (err) {
            console.warn('[IncomingTransfer] Erro ao verificar', symbol, err.message);
          }
        }

        lastCheckedBlockRef.current = currentBlock;
      } catch (err) {
        console.warn('[IncomingTransfer] Erro na verificação:', err.message);
      }
    };

    /** Varredura extra com o provider da carteira, só com aba visível (ex.: PC onde o RPC pode estar atrás).
     *  Não roda com aba oculta para não disparar "Ir para MetaMask?" no mobile. */
    const runWalletSweep = async () => {
      if (typeof document === 'undefined' || document.visibilityState !== 'visible') return;
      if (!walletProviderRef || typeof walletProviderRef.request !== 'function') return;
      try {
        const ethersProvider = new BrowserProvider(walletProviderRef);
        const currentBlock = await ethersProvider.getBlockNumber();
        const fromBlock = Math.max(0, currentBlock - WALLET_SWEEP_BLOCKS);
        const toBlock = currentBlock;
        const notified = notifiedTxHashesRef.current;

        for (const { symbol, address, decimals } of tokens) {
          try {
            const contract = new Contract(address, ERC20_ABI_WITH_TRANSFER_EVENT, ethersProvider);
            const filter = contract.filters.Transfer(null, walletAddressChecksum);
            const events = await contract.queryFilter(filter, fromBlock, toBlock);

            for (const event of events) {
              const txHash = event.transactionHash ?? event.log?.transactionHash;
              if (!txHash || notified.has(txHash)) continue;
              const raw = event.args?.value ?? event.args?.[2];
              if (raw == null) continue;
              const amount = Number(raw) / Math.pow(10, decimals);
              if (amount <= 0) continue;
              notified.add(txHash);
              const fromAddress = event.args?.[0] ?? event.args?.from ?? '';
              const payload = {
                amount,
                symbol,
                txHash,
                networkName: selectedNetwork?.name ?? '',
                chainId,
                tokenAddress: address,
                fromAddress: typeof fromAddress === 'string' ? fromAddress : (fromAddress?.toString?.() ?? ''),
                receivedAt: Date.now(),
              };
              reportIncomingTransfer(payload);
              if (typeof addNotification === 'function') {
                addNotification({ type: 'incoming_funds', ...payload });
              }
            }
          } catch (err) {
            console.warn('[IncomingTransfer] Erro no wallet sweep', symbol, err.message);
          }
        }
      } catch (err) {
        console.warn('[IncomingTransfer] Erro no wallet sweep:', err.message);
      }
    };

    const initBlock = async () => {
      try {
        const currentBlock = await readOnlyProvider.getBlockNumber();
        lastCheckedBlockRef.current = Math.max(0, currentBlock - LOOK_BACK_BLOCKS);
      } catch {
        // ignora
      }
    };

    initBlock();
    const firstPoll = setTimeout(() => {
      checkIncomingTransfers();
      runWalletSweep();
    }, FIRST_POLL_DELAY_MS);
    const intervalId = setInterval(() => {
      checkIncomingTransfers();
      runWalletSweep();
    }, POLL_INTERVAL_MS);

    const onVisibilityChange = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        checkIncomingTransfers();
        runWalletSweep();
      }
    };
    document?.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearTimeout(firstPoll);
      clearInterval(intervalId);
      document?.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [connectedWallet, isPhantomEVM, walletAddress, selectedNetwork?.chainId, selectedNetwork?.name, metamaskProvider, reportIncomingTransfer, addNotification, disableLegacyIncomingPolling]);
}
